import React, { createContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react';
import {
  CheckInState,
  CheckInAction,
  CheckInActionType,
  CheckInContextType,
  Student,
  Class,
} from './types';
import { getStudents } from '../services/api';

// Initial state
const initialState: CheckInState = {
  students: [],
  filteredStudents: [],
  selectedStudent: null,
  availableClasses: [],
  selectedClasses: [],
  searchTerm: '',
  showDropdown: false,
  showThankYou: false,
  thankYouMessage: '',
  loading: false,
  error: '',
};

// Reducer function
function checkInReducer(state: CheckInState, action: CheckInAction): CheckInState {
  switch (action.type) {
    case CheckInActionType.SET_STUDENTS:
      return {
        ...state,
        students: action.payload,
        filteredStudents: action.payload.sort((a, b) => a.name.localeCompare(b.name)),
      };

    case CheckInActionType.SET_FILTERED_STUDENTS:
      return {
        ...state,
        filteredStudents: action.payload,
      };

    case CheckInActionType.SET_SELECTED_STUDENT:
      return {
        ...state,
        selectedStudent: action.payload,
        searchTerm: action.payload?.name || '',
        showDropdown: false,
        selectedClasses: [],
        error: '',
      };

    case CheckInActionType.SET_AVAILABLE_CLASSES:
      return {
        ...state,
        availableClasses: action.payload,
        error: '',
      };

    case CheckInActionType.SET_SELECTED_CLASSES:
      return {
        ...state,
        selectedClasses: action.payload,
      };

    case CheckInActionType.TOGGLE_CLASS_SELECTION:
      // Don't allow selection of already checked-in classes
      const classToToggle = state.availableClasses.find(c => c.id === action.payload);
      if (classToToggle?.checked_in) {
        return state;
      }

      return {
        ...state,
        selectedClasses: state.selectedClasses.includes(action.payload)
          ? state.selectedClasses.filter(id => id !== action.payload)
          : [...state.selectedClasses, action.payload],
      };

    case CheckInActionType.SET_SEARCH_TERM:
      // Auto-filter students when search term changes
      const filtered = action.payload.trim() === ''
        ? state.students.sort((a, b) => a.name.localeCompare(b.name))
        : state.students.filter(student =>
            student.name.toLowerCase().includes(action.payload.toLowerCase())
          );
      
      return {
        ...state,
        searchTerm: action.payload,
        filteredStudents: filtered,
      };

    case CheckInActionType.SET_SHOW_DROPDOWN:
      return {
        ...state,
        showDropdown: action.payload,
      };

    case CheckInActionType.SET_SHOW_THANK_YOU:
      return {
        ...state,
        showThankYou: action.payload,
      };

    case CheckInActionType.SET_THANK_YOU_MESSAGE:
      return {
        ...state,
        thankYouMessage: action.payload,
        showThankYou: true,
      };

    case CheckInActionType.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case CheckInActionType.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case CheckInActionType.RESET_FORM:
      return {
        ...state,
        selectedStudent: null,
        searchTerm: '',
        showDropdown: false,
        availableClasses: [],
        selectedClasses: [],
      };

    case CheckInActionType.CLEAR_STUDENT_SELECTION:
      return {
        ...state,
        selectedStudent: null,
        searchTerm: '',
        filteredStudents: state.students.sort((a, b) => a.name.localeCompare(b.name)),
        availableClasses: [],
        selectedClasses: [],
        error: '',
      };

    default:
      return state;
  }
}

// Create context
export const CheckInContext = createContext<CheckInContextType | undefined>(undefined);

// Provider component
interface CheckInProviderProps {
  children: ReactNode;
}

export const CheckInProvider: React.FC<CheckInProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(checkInReducer, initialState);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize students on provider mount
  useEffect(() => {
    const initializeStudents = async () => {
      dispatch({ type: CheckInActionType.SET_LOADING, payload: true });
      try {
        dispatch({ type: CheckInActionType.SET_ERROR, payload: '' });
        const students = await getStudents();
        dispatch({ type: CheckInActionType.SET_STUDENTS, payload: students });
      } catch (err) {
        dispatch({ type: CheckInActionType.SET_ERROR, payload: 'Failed to load students. Please try again.' });
        console.error('Error fetching students:', err);
      } finally {
        dispatch({ type: CheckInActionType.SET_LOADING, payload: false });
      }
    };

    initializeStudents();
  }, []);

  // Action creators
  const setStudents = useCallback((students: Student[]) => {
    dispatch({ type: CheckInActionType.SET_STUDENTS, payload: students });
  }, []);

  const setSelectedStudent = useCallback((student: Student | null) => {
    dispatch({ type: CheckInActionType.SET_SELECTED_STUDENT, payload: student });
  }, []);

  const setAvailableClasses = useCallback((classes: Class[]) => {
    dispatch({ type: CheckInActionType.SET_AVAILABLE_CLASSES, payload: classes });
  }, []);

  const toggleClassSelection = useCallback((classId: number) => {
    dispatch({ type: CheckInActionType.TOGGLE_CLASS_SELECTION, payload: classId });
  }, []);

  const setSearchTerm = useCallback((term: string) => {
    dispatch({ type: CheckInActionType.SET_SEARCH_TERM, payload: term });
  }, []);

  const setShowDropdown = useCallback((show: boolean) => {
    dispatch({ type: CheckInActionType.SET_SHOW_DROPDOWN, payload: show });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: CheckInActionType.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error: string) => {
    dispatch({ type: CheckInActionType.SET_ERROR, payload: error });
  }, []);

  const resetForm = useCallback(() => {
    dispatch({ type: CheckInActionType.RESET_FORM });
  }, []);

  const clearStudentSelection = useCallback(() => {
    dispatch({ type: CheckInActionType.CLEAR_STUDENT_SELECTION });
  }, []);

  const showThankYouMessage = useCallback((message: string) => {
    dispatch({ type: CheckInActionType.SET_THANK_YOU_MESSAGE, payload: message });
  }, []);

  const hideThankYouMessage = useCallback(() => {
    dispatch({ type: CheckInActionType.SET_THANK_YOU_MESSAGE, payload: '' });
    dispatch({ type: CheckInActionType.SET_SHOW_THANK_YOU, payload: false });
  }, []);

  // Timer management function - simplified version
  const resetInteractionTimer = useCallback(() => {
    console.log('reset interaction timer called')

    // Always clear existing timer first
    if (timerRef.current) {
      console.log('clearing existing timer')
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Set a new 20-second timer (only if called when student is selected)
    console.log('setting new timer')
    const timer = setTimeout(() => {
      console.log('timer expired - clearing student selection')
      timerRef.current = null;
      dispatch({ type: CheckInActionType.CLEAR_STUDENT_SELECTION });
    }, 30000); // 30 seconds

    timerRef.current = timer;
  }, []);

  // Effect to start timer when a student is selected
  useEffect(() => {
    if (state.selectedStudent) {
      console.log('Student selected - starting inactivity timer')
      resetInteractionTimer();
    } else {
      // Clear timer when no student selected
      if (timerRef.current) {
        console.log('No student selected - clearing timer')
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [state.selectedStudent, resetInteractionTimer]);

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const contextValue: CheckInContextType = {
    state,
    dispatch,
    setStudents,
    setSelectedStudent,
    setAvailableClasses,
    toggleClassSelection,
    setSearchTerm,
    setShowDropdown,
    setLoading,
    setError,
    resetForm,
    clearStudentSelection,
    showThankYouMessage,
    hideThankYouMessage,
    resetInteractionTimer,
  };

  return (
    <CheckInContext.Provider value={contextValue}>
      {children}
    </CheckInContext.Provider>
  );
};